import React, { useEffect, useState } from 'react';
import { AdminLayout } from '../layout/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../ui/dialog';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
    Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from '../ui/accordion';
import { EmailEditor } from '../common/EmailEditor';
import { faqApi } from '../../lib/api';
import { toast } from 'sonner';
import {
    HelpCircle, Plus, Pencil, Trash2, Loader2, Save, FolderPlus,
    FolderOpen, Eye, EyeOff, User, Globe, Users,
} from 'lucide-react';
import { SortableList, SortableItem } from '../common/SortableList';

const VISIBILITY_BADGES = {
    member:      { Icon: User,   label: 'Nur Mitglieder-Portal',      cls: 'text-slate-500 bg-slate-100' },
    both:        { Icon: Users,  label: 'Mitglieder + öffentlich',    cls: 'text-emerald-700 bg-emerald-100' },
    public_only: { Icon: Globe,  label: 'Nur öffentlich (Login/Reg.)', cls: 'text-sky-700 bg-sky-100' },
};

export default function AdminFaqPage() {
    const [categories, setCategories] = useState([]);
    const [faqs, setFaqs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null); // FAQ being edited or {new: true, category_id}
    const [newCatLabel, setNewCatLabel] = useState('');

    const load = async () => {
        setLoading(true);
        try {
            const [cats, list] = await Promise.all([faqApi.listCategories(), faqApi.adminList()]);
            setCategories(cats.data || []);
            setFaqs(list.data || []);
        } finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    // ----- Reorder (Drag & Drop) -----
    // Optimistic: das lokale State-Array wird sofort umgestellt, der Server-Call
    // läuft parallel. Bei Fehler wird neu geladen.
    const reorderCategories = async (newOrderedIds) => {
        const lookup = new Map(categories.map((c) => [c.id, c]));
        const reordered = newOrderedIds.map((id, idx) => ({ ...lookup.get(id), position: idx }));
        setCategories(reordered);
        try {
            await faqApi.reorderCategories(newOrderedIds);
            toast.success('Reihenfolge gespeichert');
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Reihenfolge konnte nicht gespeichert werden');
            await load();
        }
    };

    const reorderFaqs = async (categoryId, newOrderedIds) => {
        // Update local state: set the new order/positions for the FAQs in this category
        const inCat = new Map(
            faqs.filter((f) => f.category_id === categoryId).map((f) => [f.id, f])
        );
        const newCatFaqs = newOrderedIds.map((id, idx) => ({
            ...inCat.get(id),
            category_id: categoryId,
            position: idx,
        }));
        const other = faqs.filter((f) => f.category_id !== categoryId);
        setFaqs([...other, ...newCatFaqs]);
        try {
            await faqApi.reorderFaqs(categoryId, newOrderedIds);
            toast.success('Reihenfolge gespeichert');
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Reihenfolge konnte nicht gespeichert werden');
            await load();
        }
    };

    const addCategory = async () => {
        const label = newCatLabel.trim();
        if (!label) return;
        try {
            await faqApi.createCategory(label);
            setNewCatLabel('');
            toast.success('Kategorie hinzugefügt');
            load();
        } catch (e) { toast.error(e.response?.data?.detail || 'Fehler'); }
    };

    const renameCategory = async (cat) => {
        const label = window.prompt('Neuer Name:', cat.label);
        if (!label || label === cat.label) return;
        try {
            await faqApi.updateCategory(cat.id, { label });
            toast.success('Umbenannt');
            load();
        } catch (e) { toast.error(e.response?.data?.detail || 'Fehler'); }
    };

    const deleteCategory = async (cat) => {
        if (!window.confirm(`Kategorie „${cat.label}" löschen? Funktioniert nur, wenn sie leer ist.`)) return;
        try {
            await faqApi.deleteCategory(cat.id);
            toast.success('Kategorie gelöscht');
            load();
        } catch (e) { toast.error(e.response?.data?.detail || 'Fehler'); }
    };

    const togglePublish = async (faq) => {
        try {
            await faqApi.update(faq.id, { is_published: !faq.is_published });
            load();
        } catch (e) { toast.error(e.response?.data?.detail || 'Fehler'); }
    };

    const deleteFaq = async (faq) => {
        if (!window.confirm(`FAQ „${faq.question}" wirklich löschen?`)) return;
        try {
            await faqApi.delete(faq.id);
            toast.success('Gelöscht');
            load();
        } catch (e) { toast.error(e.response?.data?.detail || 'Fehler'); }
    };

    const faqsByCat = (catId) => faqs.filter(f => f.category_id === catId).sort((a, b) => (a.position || 0) - (b.position || 0));

    return (
        <AdminLayout>
            <div className="flex flex-col gap-6">
                <div>
                    <h1 className="text-3xl font-heading font-bold flex items-center gap-2">
                        <HelpCircle className="h-7 w-7 text-primary" /> Hilfe & FAQ
                    </h1>
                    <p className="text-muted-foreground">Pflegen Sie die im Mitglieder-Portal angezeigte Hilfeseite.</p>
                </div>

                {/* Kategorien */}
                <Card>
                    <CardHeader>
                        <CardTitle className="text-base flex items-center gap-2"><FolderOpen className="h-4 w-4" /> Kategorien</CardTitle>
                        <CardDescription>Gruppieren der FAQs. Leere Kategorien werden im Portal ausgeblendet.</CardDescription>
                    </CardHeader>
                    <CardContent className="flex flex-col gap-2">
                        <SortableList items={categories.map((c) => c.id)} onReorder={reorderCategories}>
                            {(id) => {
                                const c = categories.find((x) => x.id === id);
                                if (!c) return null;
                                return (
                                    <SortableItem
                                        key={c.id}
                                        id={c.id}
                                        className="flex items-center gap-2 px-3 py-2 border rounded-lg bg-white"
                                        handleTestid={`cat-handle-${c.id}`}
                                    >
                                        <FolderOpen className="h-4 w-4 text-muted-foreground" />
                                        <span className="flex-1 text-sm font-medium" data-testid={`cat-label-${c.id}`}>{c.label}</span>
                                        <span className="text-xs text-muted-foreground">{c.faq_count || 0} FAQ(s)</span>
                                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => renameCategory(c)} title="Umbenennen">
                                            <Pencil className="h-3.5 w-3.5" />
                                        </Button>
                                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-600" onClick={() => deleteCategory(c)} title="Löschen" disabled={c.faq_count > 0}>
                                            <Trash2 className="h-3.5 w-3.5" />
                                        </Button>
                                    </SortableItem>
                                );
                            }}
                        </SortableList>
                        <div className="flex gap-2 mt-2">
                            <Input placeholder="Neue Kategorie…" value={newCatLabel} onChange={e => setNewCatLabel(e.target.value)} onKeyDown={e => e.key === 'Enter' && addCategory()} data-testid="new-cat-input" />
                            <Button onClick={addCategory} disabled={!newCatLabel.trim()} data-testid="add-cat-btn">
                                <FolderPlus className="h-4 w-4 mr-1.5" /> Kategorie
                            </Button>
                        </div>
                    </CardContent>
                </Card>

                {/* FAQs */}
                <Card>
                    <CardHeader className="flex flex-row items-center justify-between">
                        <div>
                            <CardTitle className="text-base">FAQ-Einträge</CardTitle>
                            <CardDescription>{faqs.length} Eintrag (publiziert: {faqs.filter(f => f.is_published).length})</CardDescription>
                        </div>
                        {categories.length > 0 && (
                            <Button onClick={() => setEditing({ new: true, category_id: categories[0].id, question: '', answer_html: '', is_published: false })} data-testid="add-faq-btn">
                                <Plus className="h-4 w-4 mr-1.5" /> Neue FAQ
                            </Button>
                        )}
                    </CardHeader>
                    <CardContent>
                        {loading ? (
                            <div className="text-sm text-muted-foreground"><Loader2 className="inline h-4 w-4 animate-spin mr-2" />Lade…</div>
                        ) : (
                            <div className="flex flex-col gap-4">
                                {categories.map(cat => {
                                    const items = faqsByCat(cat.id);
                                    return (
                                        <div key={cat.id} className="border rounded-lg overflow-hidden" data-testid={`cat-block-${cat.id}`}>
                                            <div className="px-3 py-2 bg-gray-50 border-b text-sm font-semibold text-gray-800 flex items-center justify-between">
                                                <span>{cat.label}</span>
                                                <span className="text-xs text-muted-foreground">{items.length}</span>
                                            </div>
                                            {items.length === 0 ? (
                                                <p className="px-3 py-2 text-xs italic text-muted-foreground">Keine Einträge</p>
                                            ) : (
                                                <div className="divide-y" data-testid={`faq-list-${cat.id}`}>
                                                    <SortableList
                                                        items={items.map((f) => f.id)}
                                                        onReorder={(newOrder) => reorderFaqs(cat.id, newOrder)}
                                                    >
                                                        {(faqId) => {
                                                            const faq = items.find((f) => f.id === faqId);
                                                            if (!faq) return null;
                                                            const vis = VISIBILITY_BADGES[faq.visibility || 'member'];
                                                            const VisIcon = vis.Icon;
                                                            return (
                                                                <SortableItem
                                                                    key={faq.id}
                                                                    id={faq.id}
                                                                    className="px-3 py-2.5 flex items-center gap-2 border-b last:border-b-0"
                                                                    handleTestid={`faq-handle-${faq.id}`}
                                                                >
                                                                    <button
                                                                        onClick={() => togglePublish(faq)}
                                                                        title={faq.is_published ? 'Veröffentlicht – Klick zum Verbergen' : 'Entwurf – Klick zum Veröffentlichen'}
                                                                        className={`shrink-0 ${faq.is_published ? 'text-emerald-600' : 'text-gray-400'}`}
                                                                        data-testid={`pub-toggle-${faq.id}`}
                                                                    >
                                                                        {faq.is_published ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                                                                    </button>
                                                                    <span
                                                                        className={`shrink-0 inline-flex items-center justify-center h-5 w-5 rounded-full ${vis.cls}`}
                                                                        title={`Sichtbarkeit: ${vis.label}`}
                                                                        data-testid={`vis-badge-${faq.id}`}
                                                                        data-visibility={faq.visibility || 'member'}
                                                                    >
                                                                        <VisIcon className="h-3 w-3" />
                                                                    </span>
                                                                    <span className="flex-1 text-sm" data-testid={`faq-question-${faq.id}`}>{faq.question}</span>
                                                                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => setEditing(faq)} title="Bearbeiten" data-testid={`edit-faq-${faq.id}`}>
                                                                        <Pencil className="h-3.5 w-3.5" />
                                                                    </Button>
                                                                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-600" onClick={() => deleteFaq(faq)} title="Löschen">
                                                                        <Trash2 className="h-3.5 w-3.5" />
                                                                    </Button>
                                                                </SortableItem>
                                                            );
                                                        }}
                                                    </SortableList>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Vorschau */}
                {faqs.filter(f => f.is_published).length > 0 && (
                    <Card>
                        <CardHeader>
                            <CardTitle className="text-base">Vorschau (Portal-Ansicht)</CardTitle>
                            <CardDescription>So sehen die Mitglieder die Hilfe-Seite.</CardDescription>
                        </CardHeader>
                        <CardContent>
                            <Accordion type="multiple" className="border rounded-lg">
                                {categories.map(cat => {
                                    const items = faqsByCat(cat.id).filter(f => f.is_published);
                                    if (items.length === 0) return null;
                                    return items.map(f => (
                                        <AccordionItem key={f.id} value={f.id}>
                                            <AccordionTrigger className="px-3 text-left">
                                                <span className="text-sm">
                                                    <span className="text-[10px] uppercase tracking-wider text-muted-foreground mr-2">{cat.label}</span>
                                                    {f.question}
                                                </span>
                                            </AccordionTrigger>
                                            <AccordionContent className="px-3">
                                                <div className="prose prose-sm max-w-none [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:list-decimal [&_ol]:pl-6 [&_li]:my-1" dangerouslySetInnerHTML={{ __html: f.answer_html || '<p class="text-muted-foreground italic">Noch keine Antwort hinterlegt</p>' }} />
                                            </AccordionContent>
                                        </AccordionItem>
                                    ));
                                })}
                            </Accordion>
                        </CardContent>
                    </Card>
                )}
            </div>

            <FaqEditDialog
                faq={editing}
                categories={categories}
                onClose={() => setEditing(null)}
                onSaved={load}
            />
        </AdminLayout>
    );
}

function FaqEditDialog({ faq, categories, onClose, onSaved }) {
    const [form, setForm] = useState({ category_id: '', question: '', answer_html: '', is_published: false, visibility: 'member' });
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (faq) {
            setForm({
                category_id: faq.category_id || categories[0]?.id || '',
                question: faq.question || '',
                answer_html: faq.answer_html || '',
                is_published: !!faq.is_published,
                visibility: faq.visibility || 'member',
            });
        }
    }, [faq, categories]);

    const submit = async () => {
        if (!form.question.trim()) { toast.error('Frage fehlt'); return; }
        setSaving(true);
        try {
            if (faq?.new) {
                await faqApi.create(form);
                toast.success('FAQ angelegt');
            } else {
                await faqApi.update(faq.id, form);
                toast.success('Gespeichert');
            }
            onSaved?.();
            onClose();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Fehler');
        } finally { setSaving(false); }
    };

    return (
        <Dialog open={!!faq} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="faq-edit-dialog">
                <DialogHeader>
                    <DialogTitle>{faq?.new ? 'Neue FAQ' : 'FAQ bearbeiten'}</DialogTitle>
                    <DialogDescription>Frage, Antwort, Kategorie und Veröffentlichungs-Status pflegen.</DialogDescription>
                </DialogHeader>
                <div className="flex flex-col gap-4">
                    <div className="grid sm:grid-cols-2 gap-3">
                        <div>
                            <Label className="text-xs">Kategorie</Label>
                            <Select value={form.category_id} onValueChange={v => setForm({...form, category_id: v})}>
                                <SelectTrigger data-testid="faq-cat-select"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {categories.map(c => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="text-xs">Sichtbarkeit</Label>
                            <Select value={form.visibility} onValueChange={v => setForm({...form, visibility: v})}>
                                <SelectTrigger data-testid="faq-visibility-select"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="member">Nur Mitglieder-Portal</SelectItem>
                                    <SelectItem value="both">Mitglieder + öffentlich</SelectItem>
                                    <SelectItem value="public_only">Nur öffentlich (Login/Registrierung)</SelectItem>
                                </SelectContent>
                            </Select>
                            <p className="text-[11px] text-muted-foreground mt-1">
                                Steuert, wo dieser Eintrag erscheint: in der Mitglieder-Hilfe, auf der öffentlichen FAQ unter <code>/hilfe</code>, oder in beiden.
                            </p>
                        </div>
                    </div>
                    <div className="flex items-end gap-2">
                        <label className="flex items-center gap-2 text-sm cursor-pointer">
                            <Switch checked={form.is_published} onCheckedChange={v => setForm({...form, is_published: v})} data-testid="faq-pub-switch" />
                            <span>Veröffentlicht</span>
                        </label>
                    </div>
                    <div>
                        <Label className="text-xs">Frage</Label>
                        <Input value={form.question} onChange={e => setForm({...form, question: e.target.value})} placeholder="z.B. Wie verlängere ich meine Lizenz?" data-testid="faq-question-input" />
                    </div>
                    <div>
                        <Label className="text-xs">Antwort</Label>
                        <EmailEditor
                            content={form.answer_html}
                            onChange={(html) => setForm({...form, answer_html: html})}
                            showPlaceholders={false}
                        />
                    </div>
                    <div className="flex justify-end gap-2 pt-2 border-t">
                        <Button variant="outline" onClick={onClose}>Abbrechen</Button>
                        <Button onClick={submit} disabled={saving || !form.question.trim()} data-testid="faq-save">
                            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                            Speichern
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
